import React from 'react';
import {
  UseFormRegister,
  RegisterOptions,
  FieldValues,
  Path,
} from 'react-hook-form';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

interface FormInputProps<TFieldValues extends FieldValues>
  extends React.ComponentPropsWithoutRef<'input'> {
  name: Path<TFieldValues>;
  register?: UseFormRegister<TFieldValues>;
  validation?: RegisterOptions<TFieldValues>;
}

/**
 * A custom input component that integrates with `react-hook-form` for form validation and state management.
 * If the `register` prop is provided, the component wires the native input to `react-hook-form`.
 * Otherwise, it acts as a simple wrapper around a native `input`, forwarding all other props.
 *
 * Text-type inputs render the shadcn `Input` (Tailwind) so styling stays consistent with the
 * design system. Checkbox/radio inputs keep a native `input` with Tailwind utilities that mirror
 * the legacy `form-check-input` positioning (absolute, `margin-top: 0.3rem`, `margin-left: -1.25rem`).
 *
 * Props:
 * - `name`: (Required) The name of the field, used for both the input element and `react-hook-form` registration.
 * - `register` (Optional): A `react-hook-form` register function for connecting the input to the form's state.
 * - `validation` (Optional): Validation rules to be applied when using `register`.
 * - Other props are forwarded directly to the underlying `input` element.
 *
 */
const FormInput = <TFieldValues extends FieldValues>({
  register,
  name,
  validation,
  className,
  type = 'text',
  ...rest
}: FormInputProps<TFieldValues>): JSX.Element => {
  const isCheckable = type === 'checkbox' || type === 'radio';

  if (isCheckable) {
    const checkableClassName = cn('absolute mt-[0.3rem] -ml-5', className);
    if (register) {
      return (
        <input
          type={type}
          className={checkableClassName}
          {...register(name, validation)}
          {...rest}
        />
      );
    }
    return (
      <input type={type} name={name} className={checkableClassName} {...rest} />
    );
  }

  if (register) {
    return (
      <Input
        type={type}
        className={className}
        {...register(name, validation)}
        {...rest}
      />
    );
  }
  return <Input type={type} name={name} className={className} {...rest} />;
};

export default FormInput;
